import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getSeoByUrl } from '../api/seo.api';
import { getActiveLanguages } from '../api/languages.api';
import { canonicalUrl } from '../config';

const DEFAULT_LANG = 'en';

/**
 * Global SEO component.
 *
 * Rendered once near the top of the app. On every route change it:
 *  - emits a canonical <link> matching the current URL (on every page), and
 *  - fetches the SEO field configured for the current path and feeds its meta
 *    tags into react-helmet when one exists.
 *
 * Page-level Helmet tags (e.g. the product detail page) are rendered deeper in
 * the tree and therefore take precedence over these defaults for matching tags.
 */
const Seo = () => {
    const location = useLocation();
    const { pathname } = location;
    const [seo, setSeo] = useState(null);
    const [languages, setLanguages] = useState([]);

    // Load the active languages from the Language Master once.
    useEffect(() => {
        let cancelled = false;

        getActiveLanguages().then((data) => {
            if (!cancelled) {
                setLanguages(data);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        // Product pages inject their own meta via ProductDetail Helmet + seoInject.
        // Skip the CMS SEO lookup to avoid duplicate/spam requests on variant URL changes.
        // About Us is likewise a single-source exception: both the server
        // (seoInject.js resolveAboutMeta) and About.jsx's own Helmet read the
        // AboutUs model, not SeoField — fetching SeoField here too would race
        // a second, different title/description against that same page.
        if (/^\/product\//i.test(pathname) || /^\/about(-us)?\/?$/i.test(pathname)) {
            setSeo(null);
            return;
        }

        let cancelled = false;

        getSeoByUrl(pathname).then((data) => {
            if (!cancelled) {
                setSeo(data);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [pathname]);

    // Resolve the html lang from the Language Master: use the page's configured
    // language only when it maps to an active master entry, otherwise default to "en".
    const lang =
        seo?.lang && languages.some((l) => l.value === seo.lang)
            ? seo.lang
            : DEFAULT_LANG;

    // Canonical: configured site origin + path only (no query/hash), in the
    // trailing-slash form the legacy site's indexed URLs use. Never derived
    // from window.location.href — staging hosts must not canonicalize to
    // themselves, and query strings must not fragment indexing signals.
    // Product PDPs own their canonical (variant slug) — skip here to avoid a
    // competing short product-slug canonical during SPA navigations.
    const isProductPage = /^\/product\//i.test(pathname);
    const canonicalHref = isProductPage ? null : canonicalUrl(pathname);

    return (
        <Helmet htmlAttributes={{ lang }}>
            {canonicalHref ? <link rel="canonical" href={canonicalHref} /> : null}
            {seo?.metaTitle ? <title>{seo.metaTitle}</title> : null}
            {seo?.metaDescription ? (
                <meta name="description" content={seo.metaDescription} />
            ) : null}
            {seo?.metaKeywords ? (
                <meta name="keywords" content={seo.metaKeywords} />
            ) : null}
            {seo?.robotsTag ? (
                <meta name="robots" content={seo.robotsTag} />
            ) : null}
            {seo?.publisher ? (
                <meta name="publisher" content={seo.publisher} />
            ) : null}
        </Helmet>
    );
};

export default Seo;
